import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react'
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser'
import { api, setAuthHeaderProvider, type UserContext } from '../api/client'
import { pca } from './msal'
import {
    AUTH_MODE,
    loginRequest,
    DEV_USERS,
    getStoredDevUser,
    setStoredDevUser,
    type DevUser,
} from './config'

export interface AuthState {
    mode: 'entra' | 'dev'
    ready: boolean
    user: UserContext | null
    accountName: string
    photoUrl: string | null
    login: () => void
    logout: () => void
    // dev-mode only
    devUsers: DevUser[]
    currentDevUserId: string
    switchDevUser: (id: string) => void
}

const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be used within AuthProvider')
    return ctx
}

/** Top-level provider — chooses the dev or Entra implementation. */
export function AuthProvider({ children }: { children: React.ReactNode }) {
    if (AUTH_MODE === 'entra' && pca) {
        return (
            <MsalProvider instance={pca}>
                <EntraAuth>{children}</EntraAuth>
            </MsalProvider>
        )
    }
    return <DevAuth>{children}</DevAuth>
}

// ---- Dev mode: header-based user switching, no tokens ----

function DevAuth({ children }: { children: React.ReactNode }) {
    const [devUser, setDevUser] = useState<DevUser>(() => getStoredDevUser())
    const [user, setUser] = useState<UserContext | null>(null)
    const [ready, setReady] = useState(false)

    // Supply X-Dev-* headers for every API call.
    useEffect(() => {
        setAuthHeaderProvider(async () => ({
            'X-Dev-User': devUser.id,
            'X-Dev-Name': devUser.name,
            'X-Dev-Email': devUser.email,
            'X-Dev-Roles': devUser.roles.join(','),
            'X-Dev-Department': devUser.department,
        }))
    }, [devUser])

    // Load the server-resolved context (isRiskManager) whenever the user changes.
    useEffect(() => {
        let cancelled = false
        setReady(false)
        api.userContext()
            .then((u) => { if (!cancelled) setUser(u) })
            .catch(() => { if (!cancelled) setUser(null) })
            .finally(() => { if (!cancelled) setReady(true) })
        return () => { cancelled = true }
    }, [devUser])

    const switchDevUser = useCallback((id: string) => {
        const next = DEV_USERS.find((u) => u.id === id) || DEV_USERS[0]
        setStoredDevUser(next.id)
        setDevUser(next)
    }, [])

    const value: AuthState = useMemo(() => ({
        mode: 'dev',
        ready,
        user,
        accountName: devUser.name,
        photoUrl: null,
        login: () => {},
        logout: () => {},
        devUsers: DEV_USERS,
        currentDevUserId: devUser.id,
        switchDevUser,
    }), [ready, user, devUser, switchDevUser])

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ---- Entra mode: MSAL access tokens ----

function EntraAuth({ children }: { children: React.ReactNode }) {
    const { instance, accounts, inProgress } = useMsal()
    const isAuthenticated = useIsAuthenticated()
    const [user, setUser] = useState<UserContext | null>(null)
    const [ready, setReady] = useState(false)
    const [photoUrl, setPhotoUrl] = useState<string | null>(null)

    const account = accounts[0]

    // Fetch the user's Microsoft 365 profile photo via Graph. Best-effort: needs
    // a silently-acquired User.Read token; on missing consent / no photo / error
    // we simply keep the initials fallback (no interactive prompt for an avatar).
    useEffect(() => {
        if (!account) {
            setPhotoUrl(null)
            return
        }
        let objectUrl: string | null = null
        let cancelled = false
        void (async () => {
            try {
                const res = await instance.acquireTokenSilent({ scopes: ['User.Read'], account })
                const resp = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
                    headers: { Authorization: `Bearer ${res.accessToken}` },
                })
                if (!resp.ok || cancelled) return // 404 = no photo set → keep initials
                objectUrl = URL.createObjectURL(await resp.blob())
                if (!cancelled) setPhotoUrl(objectUrl)
            } catch {
                /* no consent / offline / no photo — fall back to initials */
            }
        })()
        return () => {
            cancelled = true
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [instance, account])

    // Acquire an API access token for every call (silent, with redirect fallback).
    useEffect(() => {
        setAuthHeaderProvider(async (): Promise<Record<string, string>> => {
            if (!account) return {}
            try {
                const res = await instance.acquireTokenSilent({ ...loginRequest, account })
                return { Authorization: `Bearer ${res.accessToken}` }
            } catch (e) {
                if (e instanceof InteractionRequiredAuthError) {
                    await instance.acquireTokenRedirect({ ...loginRequest, account })
                }
                return {}
            }
        })
    }, [instance, account])

    useEffect(() => {
        let cancelled = false
        if (!isAuthenticated || !account) {
            setReady(true)
            return
        }
        setReady(false)
        api.userContext()
            .then((u) => { if (!cancelled) setUser(u) })
            .catch(() => { if (!cancelled) setUser(null) })
            .finally(() => { if (!cancelled) setReady(true) })
        return () => { cancelled = true }
    }, [isAuthenticated, account])

    const login = useCallback(() => { void instance.loginRedirect(loginRequest) }, [instance])
    const logout = useCallback(() => { void instance.logoutRedirect() }, [instance])

    // Automatic sign-in: as soon as MSAL is idle (redirect handling done) and no
    // account is present, redirect to Microsoft — no landing page, no button.
    // The ref guards against React StrictMode's double-invoke firing loginRedirect
    // twice ("interaction_in_progress"); inProgress===None avoids a redirect loop.
    const redirecting = useRef(false)
    useEffect(() => {
        if (!isAuthenticated && inProgress === InteractionStatus.None && !redirecting.current) {
            redirecting.current = true
            instance.loginRedirect(loginRequest).catch(() => { redirecting.current = false })
        }
    }, [isAuthenticated, inProgress, instance])

    const value: AuthState = useMemo(() => ({
        mode: 'entra',
        ready,
        user,
        accountName: account?.name || account?.username || '',
        photoUrl,
        login,
        logout,
        devUsers: [],
        currentDevUserId: '',
        switchDevUser: () => {},
    }), [ready, user, account, photoUrl, login, logout])

    // Not signed in → the effect above redirects to Microsoft automatically.
    // Show a brief branded "redirecting" screen until the navigation happens.
    if (!isAuthenticated) {
        return <RedirectingScreen />
    }
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function RedirectingScreen() {
    return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#003134] via-[#023134] to-[#04595c] p-4">
            <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/5 p-8 text-center shadow-2xl backdrop-blur">
                <img src="/Unisys_Logo_Reverse_RGB.png" alt="Unisys" className="mx-auto h-8 select-none" draggable={false} />
                <h1 className="mt-8 text-2xl font-bold tracking-tight text-white">Risk Management</h1>
                <div className="mt-8 flex items-center justify-center gap-3 text-sm text-white/70">
                    <span
                        className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-[#00E28B]"
                        aria-hidden
                    />
                    Redirecting to Microsoft sign-in&hellip;
                </div>
            </div>
        </div>
    )
}
